import { useState } from '@hookstate/core'
import React, { useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import SelectField from '../../components/Field/Select'
import TextField from '../../components/Field/Text'
import Section from '../../components/Section'
import { countryHasZipCode, useExhibitorDataState } from '../../globalStates/ExhibitorDataState'
import { useLanguageState } from '../../globalStates/LanguageState'
import { RefCountry, useReferenceDataState } from '../../globalStates/ReferenceDataState'
import { getElementFieldValue, makeSetHandler, makeValidationHandler } from '../../globalStates/utils'

function buildCountryOptions(refCountries: Array<RefCountry>) {
    return refCountries.map(refCountry => ({ id: refCountry.value, label: refCountry.name, value: refCountry.value }))
}

const BasicInfoSection: React.FC<{}> = () => {
    const branding = useLanguageState().getStrings()
    const organizationState = useExhibitorDataState().getOrganization()
    const organization = useState(organizationState)
    const refDataState = useReferenceDataState();
    const countries = useMemo(() => buildCountryOptions(refDataState.getCountries()), [refDataState])
    return (
        <Section title={branding.configuration.profilePage.sctBasicInfo.lblBasicInformation}>
            <Row>
                <Col>
                    <TextField
                        id="name"
                        name="name"
                        label={branding.configuration.profilePage.sctBasicInfo.lblCompany}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntCompany}
                        limit={255}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.companyNameMandatory}
                        validate={makeValidationHandler(organization, 'name')}
                        value={getElementFieldValue(organization, "name")}
                        onChange={makeSetHandler(organization, 'name')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.companyNameReadonly}
                    />
                </Col>
                <Col>
                    <TextField
                        id="phone"
                        name="phone"
                        label={branding.configuration.profilePage.sctBasicInfo.lblPhoneNumber}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntPhoneNumber}
                        limit={50}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.phoneNumberMandatory}
                        validate={makeValidationHandler(organization, 'phone')}
                        value={getElementFieldValue(organization, "phone")}
                        onChange={makeSetHandler(organization, 'phone')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.phoneNumberReadonly}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <TextField
                        id="mobile"
                        name="mobile"
                        label={branding.configuration.profilePage.sctBasicInfo.lblMobile}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntMobile}
                        limit={50}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.mobileNumberMandatory}
                        validate={makeValidationHandler(organization, 'mobile')}
                        value={getElementFieldValue(organization, "mobile")}
                        onChange={makeSetHandler(organization, 'mobile')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.mobileNumberReadonly}
                    />
                </Col>
                <Col>
                    <TextField
                        id="fax"
                        name="fax"
                        label={branding.configuration.profilePage.sctBasicInfo.lblFax}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntFax}
                        limit={50}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.faxMandatory}
                        validate={makeValidationHandler(organization, 'fax')}
                        value={getElementFieldValue(organization, "fax")}
                        onChange={makeSetHandler(organization, 'fax')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.faxReadonly}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <TextField
                        id="email"
                        name="email"
                        label={branding.configuration.profilePage.sctBasicInfo.lblEmail}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntEmail}
                        limit={100}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.emailMandatory}
                        validate={makeValidationHandler(organization, 'email')}
                        value={getElementFieldValue(organization, "email")}
                        onChange={makeSetHandler(organization, 'email')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.emailReadonly}
                    />
                </Col>
                <Col>
                    <TextField
                        id="website"
                        name="website"
                        label={branding.configuration.profilePage.sctBasicInfo.lblWebsite}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntWebsite}
                        limit={255}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.websiteMandatory}
                        validate={makeValidationHandler(organization, 'web')}
                        value={getElementFieldValue(organization, "web")}
                        onChange={makeSetHandler(organization, 'web')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.websiteReadonly}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <TextField
                        id="address1"
                        name="address1"
                        label={branding.configuration.profilePage.sctBasicInfo.lblAddress1}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntAddress1}
                        limit={100}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.address1Mandatory}
                        validate={makeValidationHandler(organization, 'address1')}
                        value={getElementFieldValue(organization, "address1")}
                        onChange={makeSetHandler(organization, 'address1')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.address1Readonly}
                    />
                </Col>
                <Col>
                    <TextField
                        id="address2"
                        name="address2"
                        label={branding.configuration.profilePage.sctBasicInfo.lblAddress2}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntAddress2}
                        limit={100}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.address2Mandatory}
                        validate={makeValidationHandler(organization, 'address2')}
                        value={getElementFieldValue(organization, "address2")}
                        onChange={makeSetHandler(organization, 'address2')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.address2Readonly}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <TextField
                        id="address3"
                        name="address3"
                        label={branding.configuration.profilePage.sctBasicInfo.lblAddress3}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntAddress3}
                        limit={100}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.address3Mandatory}
                        validate={makeValidationHandler(organization, 'address3')}
                        value={getElementFieldValue(organization, "address3")}
                        onChange={makeSetHandler(organization, 'address3')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.address3Readonly}
                    />
                </Col>
                <Col>
                    <TextField
                        id="postCode"
                        name="postCode"
                        label={branding.configuration.profilePage.sctBasicInfo.lblZipCode}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntZipCode}
                        limit={50}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.zipCodeMandatory || countryHasZipCode(getElementFieldValue(organization, "countryCode"))}
                        validate={makeValidationHandler(organization, 'postCode')}
                        value={getElementFieldValue(organization, "postCode")}
                        onChange={makeSetHandler(organization, 'postCode')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.zipCodeReadonly}
                    />
                </Col>
            </Row>

            <Row>
                <Col>
                    <TextField
                        id="city"
                        name="city"
                        label={branding.configuration.profilePage.sctBasicInfo.lblCity}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntCity}
                        limit={100}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.cityMandatory || countryHasZipCode(getElementFieldValue(organization, "countryCode"))}
                        validate={makeValidationHandler(organization, 'city')}
                        value={getElementFieldValue(organization, "city")}
                        onChange={makeSetHandler(organization, 'city')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.cityReadonly}
                    />
                </Col>
                <Col>
                    <SelectField
                        id="countryCode"
                        name="countryCode"
                        label={branding.configuration.profilePage.sctBasicInfo.lblCountry}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntCountry}
                        options={countries}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.countryMandatory}
                        validate={makeValidationHandler(organization, 'countryCode')}
                        value={getElementFieldValue(organization, "countryCode")}
                        onChange={makeSetHandler(organization, 'countryCode')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.countryReadonly}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextField
                        id="companySize"
                        name="companySize"
                        label={branding.configuration.profilePage.sctBasicInfo.lblCompanySize}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntCompanySize}
                        limit={255}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.companySizeMandatory}
                        validate={makeValidationHandler(organization, 'size')}
                        value={getElementFieldValue(organization, "size")}
                        onChange={makeSetHandler(organization, 'size')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.companySizeReadonly}
                    />
                </Col>
                <Col>
                    <TextField
                        id="industry"
                        name="industry"
                        label={branding.configuration.profilePage.sctBasicInfo.lblIndustry}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.hntIndustry}
                        limit={255}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.industryMandatory}
                        validate={makeValidationHandler(organization, 'industry')}
                        value={getElementFieldValue(organization, "industry")}
                        onChange={makeSetHandler(organization, 'industry')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.industryReadonly}
                    />
                </Col>
            </Row>

            <Row>
                <Col xs="6">
                    <TextField
                        id="foundingYear"
                        name="foundingYear"
                        label={branding.configuration.profilePage.sctBasicInfo.lblFoundingYear}
                        placeholder={branding.configuration.profilePage.sctBasicInfo.lblFoundingYear}
                        limit={255}
                        required={branding.configuration.profilePage.sctBasicInfoMandatory.foundingYearMandatory}
                        validate={makeValidationHandler(organization, 'foundingYear')}
                        value={getElementFieldValue(organization, "foundingYear")}
                        onChange={makeSetHandler(organization, 'foundingYear')}
                        readonly={branding.configuration.profilePage.sctBasicInfoReadonly.foundingYearReadonly}
                    />
                </Col>
            </Row>
        </Section>
    );
}

export default React.memo(BasicInfoSection)
